import { useCallback, useEffect, useReducer } from "react";

type ArgumentTypes<F extends Function> = F extends (...args: infer A) => any
  ? A
  : never;

interface ResourceFunction<F extends Function> {
  fn: F;
  cacheKey: (...args: ArgumentTypes<F>) => string[];
}

type APIFn<T> = (...args: any[]) => Promise<T>;
type APIListFn<T> = (...args: any[]) => Promise<T[]>;
type APIDeleteFn = (...args: any[]) => Promise<any>;

interface ResourceFunctions<
  T extends object,
  ReadFn extends APIFn<T>,
  ListFn extends APIListFn<T>,
  CreateFn extends APIFn<T>,
  UpdateFn extends APIFn<T>,
  DeleteFn extends APIDeleteFn
> {
  keyProp: keyof T;
  read: ResourceFunction<ReadFn>;
  list?: ResourceFunction<ListFn>;
  create?: ResourceFunction<CreateFn>;
  update?: ResourceFunction<UpdateFn>;
  delete?: ResourceFunction<DeleteFn>;
}

interface Subscribable<T> {
  result: T;
  refresh: (silent: boolean) => void;
  subscribe: (callback: () => void) => void;
  unsubscribe: (callback: () => void) => void;
}

type ResponseTypeOfSubscribable<F extends Function> = F extends (
  ...args: any
) => Subscribable<infer R>
  ? R
  : never;

/**
 * Combines the API functions related to a single CRUD resource and handles the caching / refreshing
 */
export class APIResource<
  T extends object,
  ReadFn extends APIFn<T>,
  ListFn extends APIListFn<T>,
  CreateFn extends APIFn<T>,
  UpdateFn extends APIFn<T>,
  DeleteFn extends APIDeleteFn
> {
  constructor(
    _: T,
    private apis: ResourceFunctions<
      T,
      ReadFn,
      ListFn,
      CreateFn,
      UpdateFn,
      DeleteFn
    >
  ) {}

  private cache = new Map<string, T>();
  private listCache = new Map<string, T[]>();
  private subscribers = new Map<string, Set<() => void>>();

  private subscribersForKey(key: string) {
    if (!this.subscribers.has(key)) this.subscribers.set(key, new Set());
    return this.subscribers.get(key)!;
  }

  /**
   * Read a single item
   */
  public get read() {
    return (...parameters: ArgumentTypes<ReadFn>): Subscribable<T> => {
      const cacheKeyElements = this.apis.read.cacheKey(...parameters);
      const key = cacheKeyElements[cacheKeyElements.length - 1];
      const cacheKey = cacheKeyElements.join("/");
      const listCacheKey = cacheKeyElements
        .slice(0, cacheKeyElements.length - 1)
        .concat("*")
        .join("/");
      const subscribers = this.subscribersForKey(cacheKey);

      const refresh = (silent: boolean) => {
        const promise = this.apis.read.fn(...parameters);
        this.cache.set(cacheKey, suspendify(promise));
        const redrawAll = () => this.redraw(cacheKey);
        silent ? promise.finally(redrawAll) : redrawAll();
      };

      if (!this.cache.has(cacheKey) && !this.listCache.has(listCacheKey))
        refresh(false);

      const result = this.cache.has(cacheKey)
        ? this.cache.get(cacheKey)!
        : deriveSuspended(
            () =>
              this.listCache
                .get(listCacheKey)!
                .filter((entry) => String(entry[this.apis.keyProp]) === key)[0]
          );

      return {
        result,
        refresh,
        subscribe: (callback) => subscribers.add(callback),
        unsubscribe: (callback) => subscribers.delete(callback),
      };
    };
  }

  /**
   * Get a list of all items
   */
  public get list() {
    return (...parameters: ArgumentTypes<ListFn>): Subscribable<T[]> => {
      if (!this.apis.list) throw new Error("list not implemented");

      const cacheKeyElements = this.apis.list.cacheKey(...parameters);
      const cacheKey = cacheKeyElements.concat("*").join("/");
      const subscribers = this.subscribersForKey(cacheKey);

      const refresh = (silent: boolean) => {
        const promise = this.apis.list!.fn(...parameters);
        this.listCache.set(cacheKey, suspendify(promise));

        const redrawAll = () => {
          this.redraw(cacheKey);
          // refresh all subresources
          const cacheKeyBase = cacheKeyElements.join("/");
          this.redrawSubItems(cacheKeyBase);
        };

        silent ? promise.finally(redrawAll) : redrawAll();
      };

      if (!this.listCache.has(cacheKey)) refresh(false);

      return {
        result: this.listCache.get(cacheKey)!,
        refresh,
        subscribe: (callback) => subscribers.add(callback),
        unsubscribe: (callback) => subscribers.delete(callback),
      };
    };
  }

  // Redraw all instances of an item
  private readonly redraw = (cacheKey: string) => {
    setTimeout(
      () => this.subscribers.get(cacheKey)?.forEach((callback) => callback()),
      0
    );
  };

  // Redraw all items that where refreshed when loading a list
  private readonly redrawSubItems = (cacheKey: string) => {
    setTimeout(() => {
      for (const key of this.subscribers.keys()) {
        if (key.startsWith(cacheKey) && !key.endsWith("*")) {
          this.cache.delete(key);
          this.subscribers.get(key)!.forEach((callback) => callback());
        }
      }
    }, 0);
  };

  /**
   * Create a new item
   */
  public create(...parameters: ArgumentTypes<CreateFn>): Promise<T> {
    if (!this.apis.create) throw new Error("create not implemented");

    const cacheKeyElements = this.apis.create.cacheKey(...parameters);
    const listCacheKey = cacheKeyElements.concat("*").join("/");

    const promise = this.apis.create.fn(...parameters);
    promise.then((result) => {
      const cacheKey = cacheKeyElements
        .concat(String(result[this.apis.keyProp]))
        .join("/");
      this.cache.set(cacheKey, result);

      if (this.listCache.has(listCacheKey)) {
        const list = this.listCache.get(listCacheKey)!;
        this.listCache.set(
          listCacheKey,
          deriveSuspended(() => [...list, result])
        );
      }
      this.redraw(listCacheKey);
      this.redraw(cacheKey);
    });
    return promise;
  }

  /**
   * Update an existing item
   */
  public update(...parameters: ArgumentTypes<UpdateFn>): Promise<T> {
    if (!this.apis.update) throw new Error("update not implemented");

    const cacheKeyElements = this.apis.update.cacheKey(...parameters);
    const key = cacheKeyElements[cacheKeyElements.length - 1];
    const cacheKey = cacheKeyElements.join("/");
    const listCacheKey = cacheKeyElements
      .slice(0, cacheKeyElements.length - 1)
      .concat("*")
      .join("/");

    const promise = this.apis.update.fn(...parameters);
    promise.then((result) => {
      this.cache.set(cacheKey, result);
      if (this.listCache.has(listCacheKey)) {
        const list = this.listCache.get(listCacheKey)!;
        this.listCache.set(
          listCacheKey,
          deriveSuspended(() =>
            list.map((item) =>
              String(item[this.apis.keyProp]) === key ? result : item
            )
          )
        );
      }
      this.redraw(listCacheKey);
      this.redraw(cacheKey);
    });
    return promise;
  }

  /**
   * Delete an item
   */
  public delete(...parameters: ArgumentTypes<DeleteFn>): Promise<any> {
    if (!this.apis.delete) throw new Error("delete not implemented");

    const cacheKeyElements = this.apis.delete.cacheKey(...parameters);
    const key = cacheKeyElements[cacheKeyElements.length - 1];
    const cacheKey = cacheKeyElements.join("/");
    const listCacheKey = cacheKeyElements
      .slice(0, cacheKeyElements.length - 1)
      .concat("*")
      .join("/");

    const promise = this.apis.delete.fn(...parameters);
    promise.then(() => {
      this.cache.delete(cacheKey);

      if (this.listCache.has(listCacheKey)) {
        const list = this.listCache.get(listCacheKey)!;
        this.listCache.set(
          listCacheKey,
          deriveSuspended(() =>
            list.filter((item) => String(item[this.apis.keyProp]) !== key)
          )
        );
      }
      this.redraw(listCacheKey);
      this.redraw(cacheKey);
    });
    return promise;
  }
}

/**
 * React Hook to load resources from an async API and subscribe to changes. Returns the result value and a function to trigger a refresh (refetch from server).
 * @param subscribableFn Async API function (`APIResource.read` or `APIResource.list`)
 * @param parameters Parameters of the API function
 */
export function useResource<F extends Function>(
  subscribableFn: F,
  ...parameters: ArgumentTypes<F>
): [ResponseTypeOfSubscribable<F>, (silent?: boolean) => void] {
  const {
    result,
    refresh: refreshResource,
    subscribe,
    unsubscribe,
  } = subscribableFn(...parameters) as Subscribable<
    ResponseTypeOfSubscribable<F>
  >;

  // register callback to redraw component
  const redraw = useReducer(() => ({}), {})[1] as () => void;
  useEffect(() => {
    subscribe(redraw);
    return () => unsubscribe(redraw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, redraw]);

  // create a refresh function that doesn't change between redraws
  const refresh = useCallback(
    (silent = false) => refreshResource(silent),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return [result, refresh];
}

/**
 * Convert a Promise to a Suspend compatible proxy
 * @param promise future result of an async method
 */
function suspendify<T extends object>(promise: Promise<T>): T {
  enum State {
    pending,
    done,
    error,
  }
  let state = State.pending;
  let result: T | undefined;
  let error: any | undefined;
  promise
    .then((r) => {
      state = State.done;
      result = r;
    })
    .catch((e) => {
      state = State.error;
      error = e;
    });

  const getResult = () => {
    switch (state) {
      case State.pending:
        throw promise;
      case State.error:
        throw error;
      case State.done:
        return result!;
    }
  };

  return new Proxy<T>({} as T, {
    get: (_, p: keyof T) => getResult()[p],
    has: (_, p: keyof T) => !!getResult()[p],
    set: (_, p: keyof T) => false,
    deleteProperty: () => false,
    defineProperty: () => false,
  });
}

/**
 * Lazily changes a Suspend compatible result and returns another Suspend compatible object
 * @param fn Function returning a (changed) suspended object
 */
function deriveSuspended<T extends object>(fn: () => T): T {
  let cache: T;
  function getter() {
    if (cache) return cache;
    cache = fn();
    return cache;
  }
  return new Proxy<T>({} as T, {
    get: (_, p: keyof T) => getter()[p],
    has: (_, p: keyof T) => !!getter()[p],
    set: (_, p: keyof T) => false,
    deleteProperty: () => false,
    defineProperty: () => false,
  });
}

/**
 * Transform a paginated API (Django) into one returning an array
 * @param paginatedAPI Django compatible paginated API
 */
export function unpaginate<A extends any[], R>(
  paginatedAPI: (...args: A) => Promise<Paginated<R>>
): (...args: A) => Promise<R[]> {
  return (...args: A) => {
    return paginatedAPI(...args).then((result) => {
      if (result.results.length !== result.count)
        throw new Error("server used pagination");
      return result.results;
    });
  };
}
type Paginated<T> = { count: number; results: T[] };
